import { useAuthStore } from '@terminal/stores/use-auth/store/authStore';
import wretch from 'wretch';
import AbortAddon from 'wretch/addons/abort';
import { WretchError } from 'wretch/resolver';

import { parseStreamChunk } from './parse-stream-chunk/parseStreamChunk';

export enum ApiStreamStatus {
  Success = 'success',
  Error = 'error',
  Abort = 'abort',
}

export type ApiStreamProps<T> = {
  body: object;
  onReadChunk: (chunks: T[]) => void | Promise<void>;
  onComplete?: () => void | Promise<void>;
  onError?: (error?: WretchError) => void;
  onAbort: () => void;
  headers?: (headers: Headers) => void;
  controller: AbortController;
  endpoint: string;
};

export const apiStream = async <T>({
  body,
  onReadChunk,
  onComplete,
  onError,
  headers,
  controller,
  onAbort,
  endpoint,
}: ApiStreamProps<T>) => {
  const { getState } = useAuthStore;
  const { token } = getState();

  try {
    await wretch(endpoint)
      .options({ mode: 'cors' })
      .auth(`Bearer ${token}`)
      .headers({ 'X-Request-ID': crypto.randomUUID() })
      .addon(AbortAddon())
      .signal(controller)
      .post(body)
      .onAbort(onAbort)
      .res(async response => {
        try {
          if (!response.ok || !response.body) {
            throw new Error();
          }

          headers?.(response.headers);

          const reader = response.body.getReader();
          const decoder = new TextDecoder('utf-8');
          let pendingPieces: string[] = [];

          const emit = async (lines: string) => {
            const chunks = parseStreamChunk<T>(lines);

            if (chunks.length) {
              await onReadChunk(chunks);
            }
          };

          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              // decode() with no argument flushes bytes the decoder is still
              // holding; pendingPieces covers a final record with no trailing \n.
              pendingPieces.push(decoder.decode());
              await emit(pendingPieces.join(''));
              pendingPieces = [];
              await onComplete?.();
              break;
            }

            // A single decoder with stream:true is required so multi-byte
            // characters split across two reads don't decode to U+FFFD.
            const text = decoder.decode(value, { stream: true });
            const lastNewline = text.lastIndexOf('\n');

            // Only the newly decoded text is searched, and pieces are joined
            // once a line completes. Concatenating into one buffer and scanning
            // it per read is quadratic for a record spanning many reads: the
            // backwards scan re-walks the buffer and V8 re-flattens the rope.
            if (lastNewline === -1) {
              pendingPieces.push(text);
              continue;
            }

            pendingPieces.push(text.slice(0, lastNewline));
            const completeLines = pendingPieces.join('');
            pendingPieces = [text.slice(lastNewline + 1)];

            await emit(completeLines);
          }
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            onAbort();
          }

          if (error instanceof Error && error.name !== 'AbortError') {
            onError?.();
          }
        }
      });
  } catch (error) {
    onError?.(error as WretchError);
  }
};
